import React, { FC, useMemo } from 'react';
import cx from 'classnames';

import { useSettings } from '@atlas/core';
import { Icon as IconAtlasUI } from '@atlas/ui';

import { SPECIAL_ICONS_CONFIG } from 'common/constants';
import { CDN_TYPE_CMS, imageUrlCDN } from 'common/utils/utils';
import { BUTTONS_VARIANTS } from 'components/NavigationBar/constants';
import NotificationContainer from 'components/NavigationBar/NotificationContainer';
import {
  MENU_ITEM_COUNTER_COMPONENT,
  MENU_ITEMS_WITH_COUNTER,
} from 'src/components/Pages/Menu/MenuList/constants';

import { LinkComponent } from './LinkComponent';

const getComponentIcon = (iconName: string) => {
  const match = SPECIAL_ICONS_CONFIG.find(item => item.iconName === iconName);
  return match ? match.icon : null;
};

type ButtonVariant = (typeof BUTTONS_VARIANTS)[keyof typeof BUTTONS_VARIANTS];

interface INavigationBarItem {
  altIcon?: string;
  buttonStyle?: ButtonVariant;
  edit?: string;
  icon: string;
  iconType: string;
  isSupportIcon?: boolean;
  label: string;
  lightIcon?: string;
  link: string;
  onclick?: () => void;
  target?: string;
  withNotification?: boolean;
}

const NavigationBarItem: FC<INavigationBarItem> = ({
  altIcon,
  buttonStyle = 'default',
  edit = '',
  icon,
  iconType,
  isSupportIcon,
  label,
  lightIcon,
  link,
  onclick = () => {},
  target,
  withNotification = false,
}) => {
  const { theme } = useSettings();
  const isDarkMode = theme === 'dark';

  const classNameItem = cx('navigation-tab-bar-item', { edit });
  const [forceDefaultIcon, setForceDefaultIcon] = React.useState(false);

  const isHighlighted = buttonStyle === BUTTONS_VARIANTS.highlighted;
  const isFloating = buttonStyle === BUTTONS_VARIANTS.floating;
  const isCustomMenuBtn = isHighlighted || isFloating;

  const CounterComponent = useMemo(() => {
    if (MENU_ITEMS_WITH_COUNTER.includes(link)) {
      return MENU_ITEM_COUNTER_COMPONENT[link];
    }

    return null;
  }, [link]);
  const Icon = useMemo(() => {
    if (iconType === 'custom' && !forceDefaultIcon) {
      if (isDarkMode) {
        return (
          <div className="menu-custom-icon">
            <img
              alt="menu-item"
              onError={() => setForceDefaultIcon(true)}
              role="presentation"
              src={imageUrlCDN(altIcon || lightIcon || icon, CDN_TYPE_CMS)}
            />
            {CounterComponent && <CounterComponent isCustomMenuBtn={isCustomMenuBtn} />}
          </div>
        );
      }

      if (isSupportIcon) {
        return (
          <div className="menu-custom-icon">
            <img alt="menu-item" src={imageUrlCDN(icon || altIcon, CDN_TYPE_CMS)} />
          </div>
        );
      }

      return (
        <div className="menu-custom-icon">
          <img
            alt="menu-item"
            onError={() => setForceDefaultIcon(true)}
            role="presentation"
            src={imageUrlCDN(lightIcon || altIcon || icon, CDN_TYPE_CMS)}
          />
          {CounterComponent && <CounterComponent isCustomMenuBtn={isCustomMenuBtn} />}
        </div>
      );
    }

    const mappedComponentIcon = getComponentIcon(icon);
    const fallbackClass = forceDefaultIcon ? 'i-casino' : icon;

    return (
      <div className={`menu-icon ${mappedComponentIcon ? 'menu-icon-svg' : ''}`}>
        {mappedComponentIcon ? (
          <IconAtlasUI name={mappedComponentIcon} />
        ) : (
          <span className={`bottom-${fallbackClass}`} />
        )}
        {CounterComponent && <CounterComponent />}
      </div>
    );
  }, [
    iconType,
    forceDefaultIcon,
    icon,
    isDarkMode,
    isCustomMenuBtn,
    isSupportIcon,
    lightIcon,
    altIcon,
    CounterComponent,
  ]);

  if (edit) {
    return (
      <button className={classNameItem} onClick={onclick} type="button">
        <div className="icon-container">{Icon}</div>
        <span>{label}</span>
      </button>
    );
  }

  return (
    <LinkComponent
      className={cx('button navigation-item', {
        floating: isFloating,
        highlighted: isHighlighted,
      })}
      link={link}
      target={target}
    >
      {isHighlighted || isFloating ? <div className="icon-container">{Icon}</div> : Icon}
      <NotificationContainer withNotification={withNotification}>
        <span className="label sub-title-xxsmall-300-text">{label}</span>
      </NotificationContainer>
    </LinkComponent>
  );
};

export default NavigationBarItem;
